import { all, call, fork, put, takeLatest, delay, cancel, take, cancelled } from 'redux-saga/effects';
import i18n from 'i18next';

import {
  DISABLE_API_CACHE, FETCH_COUNTRY_LIST, FETCH_REGION_LIST, FETCH_TIMEZONE_LIST
} from '../constants/ActionTypes';

import {
  showError, CommonPayload, fetchCountryListSuccess, fetchCountryListError, fetchRegionListSuccess,
  fetchRegionListError, fetchTimezoneListSuccess, fetchTimezoneListError, enableApiCache
} from '../actions/Common';
import { CustomAction } from 'model/state';
import { } from 'actions';
import { toListEntityList } from 'model/api';
import { getCountryList, getRegionList, getTimezoneList } from 'api/List';

const API_CACHE_DISABLED_FOR_MS = 40000; // disable api cache  for 40sec (cache ttl in public gateway is 30sec)

function* fetchCountryListWorker() {
  try {
    const countries = yield call(getCountryList);
    yield put(fetchCountryListSuccess(toListEntityList(countries)));
  } catch (err) {
    yield put(fetchCountryListError());
    yield put(showError(i18n.t('connection_error'), err));
  }
}

function* fetchRegionListWorker(action: CustomAction<CommonPayload>) {
  const countryId = action.payload?.countryId;
  try {
    if (countryId === undefined) {
      throw Error('Invalid Parameters');
    }
    if (countryId === null) {
      yield put(fetchRegionListSuccess([]));
    } else {
      const regions = yield call(getRegionList, countryId);
      yield put(fetchRegionListSuccess(toListEntityList(regions)));
    }
  } catch (err) {
    yield put(fetchRegionListError());
    yield put(showError(i18n.t('connection_error'), err));
  }
}

function* fetchTimezoneListWorker(action: CustomAction<CommonPayload>) {
  const countryId = action.payload?.countryId;
  const stateId = action.payload?.stateId;
  try {
    if (countryId === undefined || stateId === undefined) {
      throw Error('Invalid Parameters');
    }
    if (countryId === null) {
      yield put(fetchTimezoneListSuccess([]));
    } else {
      const timezones = yield call(getTimezoneList, countryId, stateId);
      yield put(fetchTimezoneListSuccess(toListEntityList(timezones)));
    }
  } catch (err) {
    yield put(fetchTimezoneListError());
    yield put(showError(i18n.t('connection_error'), err));
  }
}

function* enableApiCacheWorkerDelay(action: CustomAction<CommonPayload>) {
  try {
    yield delay(API_CACHE_DISABLED_FOR_MS);
    yield put(enableApiCache());
  } catch (err) {
    console.error(err);
  } finally {
    if (yield cancelled()) {
      console.log('api cache waiting process was cancelled.');
    }
  }
}

function* enableApiCacheWorker(action: CustomAction<CommonPayload>) {
  try {
    const task = yield fork(enableApiCacheWorkerDelay, action);
    yield take(DISABLE_API_CACHE);
    yield cancel(task);
  } catch (err) {
    console.error('An Error occurred while starting api cache worker', err);
  }
}

export function* fetchCountryListSaga() {
  yield takeLatest(FETCH_COUNTRY_LIST, fetchCountryListWorker);
}

export function* fetchRegionListSaga() {
  yield takeLatest(FETCH_REGION_LIST, fetchRegionListWorker);
}

export function* fetchTimezoneListSaga() {
  yield takeLatest(FETCH_TIMEZONE_LIST, fetchTimezoneListWorker);
}

export function* enableApiCacheSaga() {
  yield takeLatest(DISABLE_API_CACHE, enableApiCacheWorker);
}

export default function* commonSaga() {
  yield all([
    fork(fetchCountryListSaga),
    fork(fetchRegionListSaga),
    fork(fetchTimezoneListSaga),
    fork(enableApiCacheSaga),
  ]);
}